import type { Client } from '../services/api'

// MWA-308 — Executive search partner outreach is an executive-only service, so
// the Search Firm surface is gated on the client's tier rather than shown to
// everyone. The classification is a decision the team already makes when it
// writes the client's strategy document; this field is where that decision
// finally lives in the app.
//
// An absent tier reads as 'standard'. That is deliberate: the service is
// executive-only, so a client nobody has classified has not been sold it.
export type ClientTier = 'executive' | 'standard'

export const DEFAULT_CLIENT_TIER: ClientTier = 'standard'

export const CLIENT_TIER_OPTIONS: ReadonlyArray<{
  value: ClientTier
  label: string
  /** Shown under the field so staff know what the choice actually changes. */
  description: string
}> = [
  {
    value: 'standard',
    label: 'Standard',
    description: 'No Search Firm surface. The usual pipeline.',
  },
  {
    value: 'executive',
    label: 'Executive',
    description: 'Adds Search Firm outreach, for top-executive clients only.',
  },
]

/**
 * The one place the tier rule lives. Read this rather than comparing the field
 * directly, so later work branching on tier cannot drift from the default.
 */
export function isExecutiveClient(client?: Pick<Client, 'clientTier'> | null): boolean {
  return (client?.clientTier ?? DEFAULT_CLIENT_TIER) === 'executive'
}

/** Label for display; falls back to the default for an unclassified client. */
export function clientTierLabel(client?: Pick<Client, 'clientTier'> | null): string {
  const tier = client?.clientTier ?? DEFAULT_CLIENT_TIER
  return CLIENT_TIER_OPTIONS.find((o) => o.value === tier)?.label ?? 'Standard'
}

/** Why the Search Firm tab is unavailable, for the staff-side disabled tab. */
export const EXEC_TIER_HINT =
  'Executive clients only. Change the tier on the client record to enable this.'

// MWA-357 — Wild Card Outreach is switched on per client, independent of the
// tier: Luke runs it for "a couple of clients" (Sept 17), executive or not.
// Absent reads as off, so no client gains the tab without someone choosing to.
export function hasWildcardOutreach(client?: Pick<Client, 'wildcardOutreachEnabled'> | null): boolean {
  return client?.wildcardOutreachEnabled === true
}

/** Why the Wild Card Outreach tab is unavailable, for the staff-side disabled tab. */
export const WILDCARD_HINT =
  'Turn on Wild Card Outreach in Edit Client Info to enable this.'
